/* ==========================================================================
   BRITTEN CUSTOM: Delivery and returns page (v2) -- behaviour (one small vanilla module)
   ==========================================================================

   Added:       October 2026
   Author:      Claude / Matthew
   Location:    assets/britten-shipping-v2.js
   Loaded by:   sections/britten-shipping-v2.liquid (a deferred script tag, after the
                page markup). Nothing on the page is inline JavaScript.

   PURPOSE
   The three things on the new delivery and returns page that the server cannot do:
   real arrival dates, the wedding-date check, and "Change country". No jQuery,
   no globals. Every sentence it shows is written by the server (the data-*
   attributes on [data-bsv2-config] in snippets/britten-shipping-page-v2.liquid),
   so there is no English in this file.

   HOW IT WORKS
   1. Dates. Runs only when window.BrittenDeliveryCalc is on the page (the product
      pages' own delivery maths; on every other page it is there in calc mode while
      Britten Core is on). Otherwise the server's working-day ranges stay and the
      date field stays hidden.
      - est = BrittenDeliveryCalc.estimate(country, []): this visitor's country, a
        standard veil, today's cut-off time and the UK bank holidays. This file does
        NO date maths of its own. It uses est.standard.dates and est.priority.dates
        (the ranges printed under each option), and est.standard.maxISO and
        est.priority.maxISO (the latest arrival, printed in the headline and used
        for the wedding-date check).
      - Dates print in the page language: "Wednesday 14 October" in English (en-GB,
        as the product pages print them), "miercoles, 14 de octubre" in Spanish.
   2. Wedding-date check. Four answers, with a 14-day buffer between the latest
      estimated arrival and the wedding (data-buffer-days):
        standard arrives with the buffer to spare    -> safe to order today
        else Priority arrives with the buffer        -> order with Priority
        else Priority arrives before the day itself  -> tight, send us a message
        else                                         -> very close, send us a message
      A date in the past is treated as no date (BrittenDeliveryCalc.weddingVerdict
      says 'past'). The date is not stored anywhere.
      The field carries the iOS fixes of britten-wedding-date-check and the homepage:
      the native input is an invisible overlay on the visible field; the "Select
      date" span shows while it is empty; on touch the date is committed on blur,
      because iOS fires change the moment the picker opens.
   3. Change country. The guard-then-fallback of snippets/britten-shipping-country-bar.liquid:
      the shared location popover when window.BrittenLocPref is active, else
      Shopify's /localization form.

   THEME DEPENDENCY
   - snippets/britten-delivery-estimate.liquid (window.BrittenDeliveryCalc)
   - snippets/britten-shipping-page-v2.liquid (the markup and the data-* sentences)
   - assets/britten-shipping-v2.css (.bsv2-date__ph--set)

   CHANGELOG
   - October 2026: created (delivery and returns page v2 build).
   ========================================================================== */

(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false; // true: logs what init() found and what the date check did
  var DAY = 86400000;
  var WEEKS_FROM = 14; // a gap this many days or longer reads in weeks
  var TOUCH = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  // The country picker's market -> Shopify country code. The same map as
  // snippets/britten-shipping-country-bar.liquid ('OTHER' is the rest-of-world stand-in).
  var MARKET_TO_SHOPIFY = {
    'GB': 'GB', 'US': 'US', 'CA': 'CA', 'AU': 'AU',
    'NZ': 'NZ', 'IE': 'IE', 'ES': 'ES', 'EU': 'BE', 'OTHER': 'MC'
  };

  // === HELPER FUNCTIONS ===
  function log(message) {
    if (DEBUG_MODE && window.console) console.log('[Britten Shipping v2] ' + message);
  }

  // YYYY-MM-DD to a local date at midnight, or null.
  function parseISO(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return null;
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    return isNaN(d.getTime()) ? null : d;
  }

  function pad(n) {
    return (n > 9 ? '' : '0') + n;
  }

  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  // The page language for DISPLAY only. English prints en-GB ("Wednesday 14 October");
  // a bare "en" would give the US order.
  var LANG = (function () {
    var l = String(document.documentElement.getAttribute('lang') || 'en');
    return l.toLowerCase().indexOf('en') === 0 ? 'en-GB' : l;
  })();

  function formatter(opts) {
    try {
      return new Intl.DateTimeFormat(LANG, opts);
    } catch (e) {
      try { return new Intl.DateTimeFormat('en-GB', opts); } catch (e2) { return null; }
    }
  }
  var fmtDay = formatter({ weekday: 'long', day: 'numeric', month: 'long' });
  var fmtField = formatter({ day: 'numeric', month: 'long', year: 'numeric' });

  function dayText(d) {
    return fmtDay ? fmtDay.format(d) : d.toDateString();
  }

  function fieldText(d) {
    return fmtField ? fmtField.format(d) : d.toDateString();
  }

  // Fills a sentence template: each [token] becomes its value. Tokens in `em` are set in <em>.
  // Built from text nodes, never innerHTML, so a date or a name can never inject markup.
  function fill(el, template, values, em) {
    while (el.firstChild) el.removeChild(el.firstChild);
    var parts = String(template).split(/(\[[a-z_]+\])/);
    for (var i = 0; i !== parts.length; i++) {
      var part = parts[i];
      if (!part) continue;
      var key = /^\[([a-z_]+)\]$/.exec(part);
      if (key && Object.prototype.hasOwnProperty.call(values, key[1])) {
        if (em && em.indexOf(key[1]) !== -1) {
          var node = document.createElement('em');
          node.textContent = values[key[1]];
          el.appendChild(node);
        } else {
          el.appendChild(document.createTextNode(values[key[1]]));
        }
      } else {
        el.appendChild(document.createTextNode(part));
      }
    }
  }

  function text(template, values) {
    return String(template).replace(/\[([a-z_]+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  // === CHANGE COUNTRY ===
  // The guard-then-fallback of snippets/britten-shipping-country-bar.liquid.
  function changeShopifyCountry(marketCode, cfg) {
    var shopifyCode = MARKET_TO_SHOPIFY[marketCode] || 'GB';
    // A visitor already in an EU-zone country who picks "European Union" stays in their own
    // country: the BE stand-in is for a visitor from outside the EU zone only.
    if (marketCode === 'EU' && cfg.euZone && cfg.country) shopifyCode = cfg.country;

    // If the shared location-preferences popover is active, open it pre-selected to this
    // country instead of submitting straight away, so the shopper can also pick a language.
    if (window.BrittenLocPref && window.BrittenLocPref.active) {
      window.BrittenLocPref.open(shopifyCode);
      return;
    }

    var localizationForm = document.querySelector('form[action="/localization"]');

    if (localizationForm) {
      var input = localizationForm.querySelector('input[name="country_code"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'country_code';
        localizationForm.appendChild(input);
      }
      input.value = shopifyCode;
      localizationForm.submit();
      return;
    }

    // Fallback: no localization form on the page (rare).
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    [
      { name: 'country_code', value: shopifyCode },
      { name: '_method', value: 'PUT' },
      { name: 'return_to', value: window.location.pathname }
    ].forEach(function (f) {
      var i = document.createElement('input');
      i.type = 'hidden';
      i.name = f.name;
      i.value = f.value;
      form.appendChild(i);
    });
    document.body.appendChild(form);
    form.submit();
  }

  function initCountry(root, cfg) {
    var select = root.querySelector('[data-bsv2-country-select]');
    if (!select) return;
    select.addEventListener('change', function () {
      changeShopifyCountry(this.value, cfg);
    });
  }

  // === DATES AND THE WEDDING-DATE CHECK ===
  function initDates(root, cfg) {
    var calc = window.BrittenDeliveryCalc;
    if (!calc || typeof calc.estimate !== 'function') {
      log('dates: no delivery calculation, the working-day ranges stay');
      return;
    }

    var est = null;
    try { est = calc.estimate(cfg.country || undefined, []); } catch (e) { log('dates: estimate failed: ' + e.message); }
    var std = est && est.standard ? parseISO(est.standard.maxISO) : null;
    if (!std) {
      log('dates: no standard date, the working-day ranges stay');
      return;
    }
    var rush = est.priority && est.priority.maxISO ? parseISO(est.priority.maxISO) : null;

    // The range under each option ("28 October - 4 November"), from the calculation.
    var arrivals = root.querySelectorAll('[data-bsv2-arrival]');
    for (var i = 0; i !== arrivals.length; i++) {
      var tier = arrivals[i].getAttribute('data-bsv2-arrival') === 'priority' ? est.priority : est.standard;
      if (tier && tier.dates && cfg.arrival) arrivals[i].textContent = text(cfg.arrival, { dates: tier.dates });
    }

    // The headline: the latest estimated arrival of each option.
    var headline = root.querySelector('[data-bsv2-headline]');
    if (headline) {
      if (rush && cfg.headline) {
        fill(headline, cfg.headline, { std: dayText(std), priority_date: dayText(rush), priority: cfg.priority }, ['std', 'priority_date']);
      } else if (cfg.headlineStd) {
        fill(headline, cfg.headlineStd, { std: dayText(std) }, ['std']);
      }
    }

    var box = root.querySelector('[data-bsv2-date]');
    if (!box) return;
    var field = box.querySelector('[data-bsv2-date-field]');
    var input = box.querySelector('[data-bsv2-date-input]');
    var ph = box.querySelector('[data-bsv2-date-ph]');
    var out = box.querySelector('[data-bsv2-date-out]');
    if (!field || !input || !ph || !out) return;

    var emptyText = ph.textContent;
    var committed = null; // the date the sentence answers
    var pending = null; // touch: the value held while the picker is open

    function verdictFor(iso) {
      if (typeof calc.weddingVerdict !== 'function') return null;
      try { return calc.weddingVerdict(est, iso); } catch (e) { return null; }
    }

    // "3 weeks" from 14 days, else "9 days" / "1 day".
    function gapText(from, need) {
      var days = Math.round((need.getTime() - from.getTime()) / DAY);
      if (days >= WEEKS_FROM) return text(cfg.gapWeeks, { n: Math.floor(days / 7) });
      return days === 1 ? cfg.gapDay : text(cfg.gapDays, { n: days });
    }

    function answer(iso) {
      var need = iso ? parseISO(iso) : null;
      if (!need) return cfg.datePrompt;
      var buffer = cfg.bufferDays * DAY;
      if (std.getTime() + buffer <= need.getTime()) {
        return text(cfg.dateSafe, { date: dayText(std), gap: gapText(std, need) });
      }
      if (rush && rush.getTime() + buffer <= need.getTime()) {
        return text(cfg.datePriority, { priority: cfg.priority, date: dayText(rush), gap: gapText(rush, need) });
      }
      if (rush && need.getTime() > rush.getTime()) {
        return text(cfg.dateTight, { priority: cfg.priority, date: dayText(rush), gap: gapText(rush, need) });
      }
      return cfg.dateClose;
    }

    function commit(value) {
      var iso = parseISO(value) ? value : null;
      var past = iso && verdictFor(iso) === 'past';
      committed = iso;

      // The visible field shows what she chose; the native input is invisible.
      ph.textContent = iso ? fieldText(parseISO(iso)) : emptyText;
      ph.classList.toggle('bsv2-date__ph--set', !!iso);

      out.textContent = answer(iso && !past ? iso : null);
      log('date: ' + (iso || 'cleared'));
    }

    input.min = todayISO();

    input.addEventListener('change', function () {
      var v = input.value || null;
      if (TOUCH && document.activeElement === input) {
        pending = { v: v }; // the picker is still open: commit on blur, not now
        return;
      }
      commit(v);
    });

    input.addEventListener('blur', function () {
      if (!TOUCH) return;
      var held = pending;
      pending = null;
      var v = held ? held.v : (input.value || null);
      // Opened and dismissed with no change: nothing to do.
      if (held || v !== committed) commit(v);
    });

    // The input is an invisible overlay, so a click anywhere on the field opens the picker.
    input.addEventListener('click', function () {
      try { input.showPicker(); } catch (e) { /* older browsers open it themselves */ }
    });

    commit(null);
    box.hidden = false;
    out.setAttribute('aria-live', 'polite');

    log('dates: ready, standard ' + est.standard.maxISO + (rush ? ', priority ' + est.priority.maxISO : ', no priority'));
  }

  // === MAIN ===
  function readConfig(el) {
    function get(name) { return el.getAttribute('data-' + name) || ''; }
    return {
      country: get('country'),
      euZone: get('eu-zone') === 'true',
      bufferDays: parseInt(get('buffer-days'), 10) || 14,
      priority: get('priority'),
      arrival: get('arrival'),
      headline: get('headline'),
      headlineStd: get('headline-std'),
      datePrompt: get('date-prompt'),
      dateSafe: get('date-safe'),
      datePriority: get('date-priority'),
      dateTight: get('date-tight'),
      dateClose: get('date-close'),
      gapWeeks: get('gap-weeks'),
      gapDay: get('gap-day'),
      gapDays: get('gap-days')
    };
  }

  function init() {
    if (!FEATURE_ENABLED) return;
    var root = document.querySelector('[data-bsv2]');
    if (!root) {
      log('no [data-bsv2] on this page');
      return;
    }
    var holder = root.querySelector('[data-bsv2-config]');
    if (!holder) {
      log('no [data-bsv2-config], nothing to do');
      return;
    }
    var cfg = readConfig(holder);
    initCountry(root, cfg);
    initDates(root, cfg);
  }

  // The delivery calculation is rendered at the end of the page; wait for the document.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
